import { type SbBlokData } from "@storyblok/react";
import { z } from "zod";
import { ButtonAtomProps } from "../../atoms/button";
import { ImageGraphicAtomProps } from "../../atoms/image-graphic";
import { TextCardProps } from "../../molecules/text-card";
import { AssetStoryblok } from "./asset-storyblok";
import { StoryblokLink } from "./storyblok-link";
import { PaymentScheduleFrequency } from "../../../generated/graphql";

export enum WojoInsuranceSortBy {
    PRICE_HIGHEST = "PRICE_HIGHEST",
    PRICE_LOWEST = "PRICE_LOWEST",
}

export type WojoCheckoutFooterConfig = {
    component: "wojo-checkout-footer";
    links?: WojoLink[];
    registrationNumbers?: string;
} & SbBlokData;

export type WojoCheckoutConfig = {
    footer: WojoCheckoutFooterConfig;
    shouldCollectTravelerAge: boolean;
    shouldCollectTravelerGender: boolean;
    shouldCollectTravelerNationality: boolean;
    shouldCollectTravelerMailingAddress: boolean;
    enableRoommateMatching: boolean;
    enableCountryMailingAddressChange: boolean;
    enableRoommateCollectionAtConfirmation: boolean;
    insuranceSortBy: WojoInsuranceSortBy;
    cancellationMessaging?: string;
    cancellationMessageModalContent?: string;
    insuranceLimitToUsCitizensOnlyCriteria?:
        | "mailingAddress"
        | "passportCountryOfIssue"
        | undefined;
    badgedInsurancePlan?: string;
    insuranceBadgeText?: string;
    component: "wojo-checkout-config";
} & SbBlokData;

export type WojoExternalAccreditation = {
    component: "wojo-external-accreditation";
    darkModeImageSrc?: string;
    imageAlt?: string;
    lightModeImageSrc?: string;
    link?: string;
} & SbBlokData;

type WojoImageGraphicAtom = {
    component: "wojo-image-graphic-atom";
} & ImageGraphicAtomProps;

export type WojoAssetLink = {
    component: "wojo-asset-link";
    link: StoryblokLink;
    lightModeAsset: AssetStoryblok;
    darkModeAsset: AssetStoryblok;
} & SbBlokData;

export type WojoFooterConfig = {
    accreditations?: (WojoImageGraphicAtom | WojoExternalAccreditation)[];
    additionalTermsAndConditionsText?: string;
    component: "wojo-footer-config";
    emailSignUpTitle?: string;
    mainNav?: WojoNavGroupColumn[];
    marketSection?: WojoFooterMarketSection[];
    registrationNumbers?: string;
    socialLinks?: WojoFooterSocialLink[];
    utilityNav?: WojoLink[];
    mainBrand?: [WojoAssetLink];
    familyBrandMarketing?: string;
    familyBrands?: WojoAssetLink[];
} & SbBlokData;

export type WojoFooterMarketSection = {
    component: "wojo-footer-market-section";
    flagIcon?: WojoImageGraphicAtom[];
    siteLink?: WojoLink[];
    text?: string;
} & SbBlokData;

export type WojoFooterSocialLink = {
    component: "wojo-footer-social-link";
    icon?: WojoImageGraphicAtom[];
    link?: StoryblokLink;
} & SbBlokData;

export type WojoHeaderConfig = {
    component: "wojo-header-config";
    logo: WojoImageGraphicAtom[];
    logoLink: StoryblokLink;
    topLinks?: WojoLink[];
    mainNav?: WojoNavGroup[];
    getHelpLink?: StoryblokLink;
    searchPlaceholder?: string;
    showPhoneOrWishlist?: string;
} & SbBlokData;

export type WojoLink = {
    component: "wojo-link";
    link?: StoryblokLink;
    text?: string;
} & SbBlokData;

export type WojoNavGroup = {
    component: "wojo-nav-group";
    columns?: WojoNavGroupColumn[];
    title?: string;
    link?: StoryblokLink;
    cta?: ButtonAtomProps[];
};

export type WojoNavGroupColumn = {
    columnTitle?: string;
    component: "wojo-nav-group-column";
    items?: WojoLink[];
} & SbBlokData;

export type GlobalMerchandisingCard = {
    merchandisingCard: [TextCardProps];
    hideMerchandisingCard: boolean;
    merchandisingCardPlacement: string;
};

export type PriceFilterRange = {
    end?: string;
    label: string;
    start?: string;
};

export type PhoneApp = {
    storeLink: StoryblokLink;
    storeImage: WojoImageGraphicAtom[];
};

export type WebConfigStory = {
    footer: WojoFooterConfig;
    header: WojoHeaderConfig;
    iosApp: PhoneApp[]; // required
    androidApp: PhoneApp[]; // required
    callToBookDpd?: string;
    priceFilterRanges?: PriceFilterRange[];
    globalMerchandisingCard?: GlobalMerchandisingCard[];
    defaultPaymentFrequency?: PaymentScheduleFrequency;
    smsOptInDefault: boolean;
};

export const WojoAccountConfigSchema = z.object({
    component: z.literal("wojo-account-config"),
    displayClassLevel: z.boolean(),
    enablePdfDownload: z.boolean(),
    enableFlyWithFriends: z.boolean(),
    numberOfDaysQuoteIsVisible: z.coerce.number().optional(),
});

export type WojoAccountConfig = z.infer<typeof WojoAccountConfigSchema>;
